import { For, Show } from 'solid-js';
import Text from './Text';
import ItemStackView from './ItemStackView';
import { useTranslation } from '../hooks/useTranslation';
import { useItemStacks } from '../hooks/useItemStacks';
import { useItemName } from '../hooks/useItemName';
import { formatQuantity } from '../services/quantityFormatter';
import { formatDuration } from '../services/durationFormat';
import type { CraftPlanIngredient, CraftPlanStep, CraftShortfall, CraftStepDisposition, CustomItemDefinition } from '../services/inventoryApi';
import styles from './CraftPlanView.module.css';

const DISPOSITION_KEYS: Record<CraftStepDisposition, string> = {
    Final: 'INVENTORY_CRAFT_STEP_FINAL',
    Parked: 'INVENTORY_CRAFT_STEP_PARKED',
    Buffered: 'INVENTORY_CRAFT_STEP_BUFFERED',
};

const dispositionKey = (disposition: CraftStepDisposition) => DISPOSITION_KEYS[disposition] ?? DISPOSITION_KEYS.Parked;

// A buffered step is crafted inside the round of the step below it, so the numbering counts the rows
// the bot actually walks to a chest for.
const phaseNumber = (steps: CraftPlanStep[], step: CraftPlanStep) =>
    steps.filter(candidate => candidate.disposition !== 'Buffered').indexOf(step) + 1;

export const phaseCount = (steps: CraftPlanStep[]) =>
    steps.filter(step => step.disposition !== 'Buffered').length;

export interface CraftPlanViewProps {
    steps: CraftPlanStep[];
    shortfalls: CraftShortfall[];
    totalConsumed: CraftPlanIngredient[];
    estimatedSeconds: number;
    planError?: string | null;
    customItems: CustomItemDefinition[];
    /** The order page already shows the estimate on its line row, so it hides the one above the steps. */
    hideStepsHeader?: boolean;
}

/// One craft plan, rendered the same way wherever it is shown: what is missing, the phases in the
/// order the bot runs them with their buffered steps nested, and what leaves the source storage.
export default function CraftPlanView(props: CraftPlanViewProps) {
    const { t } = useTranslation();
    const { nameFor, itemStack } = useItemStacks(() => props.customItems);
    const { stackSize } = useItemName();

    return (
        <>
            <Show when={props.planError}>
                <p class={styles.blocking}><i class="fa-solid fa-triangle-exclamation" /> {props.planError}</p>
            </Show>

            <Show when={props.shortfalls.length > 0}>
                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span class={styles.fieldLabel}><Text val="INVENTORY_CRAFT_MISSING" /></span>
                    <For each={props.shortfalls}>{(shortfall) => (
                        <div class={styles.shortfallRow}>
                            <ItemStackView stack={itemStack(shortfall.itemKey)} size={32} />
                            <span class={styles.stepName}>{nameFor(shortfall.itemKey)}</span>
                            <span class={styles.shortfallNeed}>
                                {t('INVENTORY_CRAFT_MISSING_DETAIL', formatQuantity(shortfall.needed, stackSize(shortfall.itemKey)), formatQuantity(shortfall.available, stackSize(shortfall.itemKey)))}
                            </span>
                        </div>
                    )}</For>
                </div>
            </Show>

            <Show when={props.steps.length > 0}>
                <div class="v" style={{ gap: 'var(--spacing-sm)' }}>
                    <Show when={!props.hideStepsHeader}>
                        <div class="row" style={{ 'justify-content': 'space-between', gap: 'var(--spacing-sm)' }}>
                            <span class={styles.fieldLabel}>
                                {t('INVENTORY_CRAFT_STEPS', String(phaseCount(props.steps)))}
                            </span>
                            <Show when={formatDuration(props.estimatedSeconds)}>{(planEta) => (
                                <span class={styles.stepTarget}>{t('INVENTORY_CRAFT_PLAN_ETA', planEta())}</span>
                            )}</Show>
                        </div>
                    </Show>
                    <For each={props.steps}>{(step) => (
                        <div classList={{ [styles.stepRow]: true, [styles.stepRowBuffered]: step.disposition === 'Buffered' }}>
                            <span class={styles.stepIndex}>{step.disposition === 'Buffered' ? '' : phaseNumber(props.steps, step)}</span>
                            <ItemStackView stack={itemStack(step.itemKey)} size={32} />
                            <div class="v" style={{ gap: 'var(--spacing-xs)', 'min-width': 0 }}>
                                <span class={styles.stepName}>
                                    {t('INVENTORY_CRAFT_STEP_MAKES', formatQuantity(step.times), formatQuantity(step.producedQuantity, stackSize(step.itemKey)), nameFor(step.itemKey))}
                                </span>
                                <span class={styles.stepIngredients}>
                                    {t('INVENTORY_CRAFT_STEP_CONSUMES', step.ingredients.map(ingredient => `${formatQuantity(ingredient.quantity, stackSize(ingredient.itemKey))} ${nameFor(ingredient.itemKey)}`).join(', '))}
                                </span>
                                <span class={styles.stepTarget}>
                                    <Text val={dispositionKey(step.disposition)} />
                                </span>
                            </div>
                            <Show when={step.disposition !== 'Buffered' && formatDuration(step.estimatedSeconds)}>{(stepEta) => (
                                <span class={styles.stepEta}>{t('INVENTORY_CRAFT_STEP_ETA', stepEta())}</span>
                            )}</Show>
                        </div>
                    )}</For>
                </div>
            </Show>

            <Show when={props.totalConsumed.length > 0}>
                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span class={styles.fieldLabel}><Text val="INVENTORY_CRAFT_TOTAL_CONSUMED" /></span>
                    <For each={props.totalConsumed}>{(consumed) => (
                        <div class={styles.stepRow}>
                            <ItemStackView stack={itemStack(consumed.itemKey)} size={32} />
                            <span class={styles.stepName}>{nameFor(consumed.itemKey)}</span>
                            <span class={styles.consumedAmount}>{formatQuantity(consumed.quantity, stackSize(consumed.itemKey))}</span>
                        </div>
                    )}</For>
                </div>
            </Show>
        </>
    );
}
